import { isRecordingActive } from "./recording.js";
import type { RecordingStatus } from "./recording.js";
import type { LoadResult } from "./script-source.js";

/**
 * Whether the refresh button is live, and what it says either way.
 *
 * The rule the desktop's control window applies (FR-17), written once for every
 * host that draws a refresh control. It is a function rather than three
 * conditions inside a component because the answer is the same on all three and
 * the case that matters is the one hardest to reach from a screen — "refresh is
 * disabled while recording" is precisely the rule whose absence is discovered by
 * destroying a take.
 *
 * It lived in `packages/web/page` until mobile-app ticket 14, which is when the
 * phone grew a document to re-fetch and therefore a second control that had to
 * mean the same thing. The reasons and the wording came with it: a phone that
 * greyed its own key with its own sentence would be a second opinion about the
 * same rule.
 */
export interface RefreshAvailability {
  enabled: boolean;
  /** The button's title, which is the only place a disabled control can explain
   * itself. */
  reason: string;
}

/**
 * Why the script cannot be replaced right now, which is the take.
 *
 * A name of its own since mobile-app ticket 26, when a second thing had to say
 * it: the rule was never about fetching, it is about swapping the script the
 * recorder is naming and the prompter is anchored in, and a *load* that arrives
 * mid-take is the same swap. The host that refuses one answers with this rather
 * than with a sentence of its own, for the reason the phone's key took this
 * file's wording in the first place.
 */
export const UNAVAILABLE_WHILE_RECORDING =
  "Refresh is unavailable while recording";

/**
 * What a `loadScript` is answered while a take is active — or a load or refresh
 * whose fetch came back after one began.
 *
 * One object since mobile-app ticket 31, when the web and the desktop came to
 * refuse what the phone already did: three hosts spelling the same three fields
 * is three chances to disagree about the reason. `ScriptController` gives it
 * since ticket 35, which is what moved the rule out of the hosts. A `LoadResult`,
 * so every screen that asked draws it where it draws any other load that failed.
 */
export const LOAD_REFUSED_WHILE_RECORDING: LoadResult = Object.freeze({
  ok: false,
  reason: "recording",
  message: UNAVAILABLE_WHILE_RECORDING,
});

export interface RefreshInputs {
  /** Whether there is a script to re-fetch at all. */
  loaded: boolean;
  recording: Pick<RecordingStatus, "phase">;
  /** Whether a refresh is already on its way. */
  inFlight: boolean;
}

export function refreshAvailability({
  loaded,
  recording,
  inFlight,
}: RefreshInputs): RefreshAvailability {
  if (inFlight) return { enabled: false, reason: "Refreshing…" };
  // Checked before `loaded` so that the mid-take answer is the mid-take one
  // whatever else is true — there is no state in which "you are recording" is
  // the less useful thing to say.
  if (isRecordingActive(recording.phase)) {
    return { enabled: false, reason: UNAVAILABLE_WHILE_RECORDING };
  }
  if (!loaded) return { enabled: false, reason: "Load a script first" };
  return { enabled: true, reason: "Re-fetch the document and keep your place" };
}
